import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import { useLoading } from '../../context/LoadingContext';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  Upload,
  Paperclip,
  History,
  Download,
  CheckCircle2,
  X,
  FileText,
  FileUp,
  Image as ImageIcon,
  Loader2,
  Save as SaveIcon,
  User,
  Hash,
  Briefcase,
  Calendar,
  AlertCircle,
  Tag,
  Info
} from 'lucide-react';
import styles from './UploadDesign.module.css';

const UploadDesign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [mainFile, setMainFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Data State
  const [vendors, setVendors] = useState([]);
  const [allLOIs, setAllLOIs] = useState([]);
  const [filteredLOIs, setFilteredLOIs] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [loiDetails, setLoiDetails] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    drawingType: 'Architectural Plans',
    version: '',
    revisionCode: '',
    submissionNotes: '',
    internalNotes: '',
    vendorReceivedDate: '',
    clientSubmissionDate: '',
    consultantSubmissionDate: ''
  });

  const mainFileInputRef = useRef(null);
  const supportingFilesInputRef = useRef(null);

  useEffect(() => {
    fetchVendors();
    fetchAllLOIs();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  useEffect(() => {
    if (mainFile) {
      const url = URL.createObjectURL(mainFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [mainFile]);

  // Handle cross-navigation from LOI Drawer
  useEffect(() => {
    if (location.state?.po && vendors.length > 0 && allLOIs.length > 0) {
      const po = location.state.po;
      const vendorOption = vendors.find(v => Number(v.value) === Number(po.vendor_id));
      if (vendorOption) {
        setSelectedVendor(vendorOption);
        const loisForVendor = allLOIs.filter(p => Number(p.vendor_id) === Number(po.vendor_id));
        setFilteredLOIs(loisForVendor.map(p => ({ value: p.id, label: p.loi_number, ...p })));

        const loiOption = { value: po.id, label: po.loi_number, ...po };
        setSelectedPO(loiOption);
        setLoiDetails(po);
        fetchVersionHistory(po.id);
      }
    }
  }, [location.state, vendors, allLOIs]);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors-dropdown');
      if (response.data.success) {
        setVendors(response.data.data.map(v => ({ value: v.id, label: v.vendor_name })));
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchAllLOIs = async () => {
    try {
      const response = await api.get('/letter-of-intents', { params: { per_page: 1000 } });
      const lois = response.data.data?.data || response.data.data || [];
      setAllLOIs(lois);
    } catch (error) {
      console.error('Error fetching LOIs:', error);
    }
  };

  const fetchVersionHistory = async (loiId, submissionId = null) => {
    try {
      // 1. Fetch version history records
      if (submissionId) {
        const historyResponse = await api.get(`/design-submissions/${submissionId}/history`);
        const historyData = historyResponse.data.data || historyResponse.data || [];
        setVersionHistory(Array.isArray(historyData) ? historyData : []);
      } else {
        setVersionHistory([]);
      }

      // 2. Fetch suggest next version from the dedicated API
      const nextVersionRes = await api.get(`/design-submissions/next-version/${loiId}`);
      if (nextVersionRes.data.success) {
        setFormData(prev => ({
          ...prev,
          version: nextVersionRes.data.version_number
        }));
      } else if (nextVersionRes.data.message) {
        toast.error(nextVersionRes.data.message);
      }
    } catch (error) {
      console.error('Error fetching design version info:', error);

      const errorMessage = error.response?.data?.message;
      if (errorMessage === "intimate to vendor missing") {
        toast.error("Design Submission Error: " + errorMessage);
      } else {
        toast.error('Failed to fetch design version info');
      }

      setVersionHistory([]);
      // Fallback if API fails
      setFormData(prev => ({ ...prev, version: '1.0' }));
    }
  };

  const handleVendorChange = (option) => {
    setSelectedVendor(option);
    setSelectedPO(null);
    setLoiDetails(null);
    setVersionHistory([]);
    if (option) {
      const filtered = allLOIs.filter(po => Number(po.vendor_id) === Number(option.value));
      setFilteredLOIs(filtered.map(p => ({ value: p.id, label: p.loi_number, ...p })));
    } else {
      setFilteredLOIs([]);
    }
  };

  const handleLOIChange = async (option) => {
    setSelectedPO(option);
    if (option) {
      setLoiDetails(option);
      // Check if the LOI already has an existing design submission to fetch its chain
      // If the backend doesn't provide this yet, it defaults to null
      const latestSubmissionId = option.latest_design_submission?.id;
      fetchVersionHistory(option.value, latestSubmissionId);
    } else {
      setLoiDetails(null);
      setVersionHistory([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toLocaleDateString('en-CA') : ''
    }));
  };

  const handleMainFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainFile(file);
    }
  };

  const handleSupportingFilesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSupportingFiles([...supportingFiles, ...files]);
    }
  };

  const removeMainFile = (e) => {
    e.stopPropagation();
    setMainFile(null);
    if (mainFileInputRef.current) {
      mainFileInputRef.current.value = '';
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setMainFile(file);
    }
  };

  const handleDownload = async (submission) => {
    try {
      const response = await api.get(`/design-submissions/${submission.id}/file`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // prioritize pdf_filename from the history object
      const fileName = submission.pdf_filename || submission.pdf_filepath?.split('/').pop() || 'design.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleSubmit = async (e, shouldAddAnother = false) => {
    if (e) e.preventDefault();
    if (!selectedVendor || !selectedPO) {
      toast.error('Please select both Vendor and LOI (Letter of Intent)');
      return;
    }


    const formDataObj = new FormData();
    formDataObj.append('letter_of_intent_id', selectedPO.value);
    formDataObj.append('submitted_by_vendor_id', selectedVendor.value);
    formDataObj.append('submission_date', new Date().toISOString().split('T')[0]);
    formDataObj.append('status', 'in_review');
    formDataObj.append('version_number', formData.version);
    formDataObj.append('revision_code', formData.revisionCode);
    formDataObj.append('submission_notes', formData.submissionNotes);
    formDataObj.append('pdf_filepath', mainFile || '');
    formDataObj.append('notes', formData.internalNotes);
    formDataObj.append('drawing_type', formData.drawingType);
    if (formData.vendorReceivedDate) {
      formDataObj.append('receive_from_vendor_date', formData.vendorReceivedDate);
    }
    if (formData.clientSubmissionDate) {
      formDataObj.append('submission_to_client_date', formData.clientSubmissionDate);
    }
    if (formData.consultantSubmissionDate) {
      formDataObj.append('submission_to_consultant_date', formData.consultantSubmissionDate);
    }

    try {
      const response = await api.post('/design-submissions', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 201 || response.status === 200) {
        toast.success('Design submission uploaded successfully');

        if (shouldAddAnother) {
          // Reset form for next upload
          setMainFile(null);
          setFormData(prev => ({
            ...prev,
            submissionNotes: '',
            internalNotes: '',
            revisionCode: '',
            clientSubmissionDate: ''
          }));
          // Refresh history for the same LOI
          fetchVersionHistory(selectedPO.value);
        } else {
          navigate('/projects/design-submissions');
        }
      }
    } catch (error) {
      console.error('Error uploading design:', error);
      toast.error(error.response?.data?.message || 'Failed to upload design');
    }
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-pending';
    switch (status.toLowerCase()) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'in_revision':
      case 'revision': return 'status-revision';
      case 'in_review': return 'status-pending';
      default: return 'status-pending';
    }
  };

  return (
    <div className={styles['upload-design-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title']}>
              <h1>Upload Design Against LOI</h1>
              <p>Submit engineering drawings and technical documentation for review against specific LOIs (Letter of Intent).</p>
            </div>
          </div>

          <div className={[styles['selection-card'], styles['card'], styles['premium-selection']].join(' ')}>
            <div className={styles['selection-header-row']}>
              <div className={styles['selection-icon-box']}>
                <FileUp size={20} />
              </div>
              <div className={styles['selection-texts']}>
                <h3>Source Selection</h3>
                <p>Choose the Vendor and relevant LOI to upload documentation against</p>
              </div>
            </div>
            <div className={styles['selection-grid']}>
              <div className={styles['form-group']}>
                <label><User size={16} /> VENDOR <span className={styles['required-star']}>*</span></label>
                <CustomSelect
                  options={vendors}
                  value={selectedVendor}
                  onChange={handleVendorChange}
                  placeholder="Choose Vendor..."
                />
              </div>
              <div className={styles['form-group']}>
                <label><Hash size={16} /> LOI (LETTER OF INTENT) <span className={styles['required-star']}>*</span></label>
                <CustomSelect
                  options={filteredLOIs}
                  value={selectedPO}
                  onChange={handleLOIChange}
                  placeholder={selectedVendor ? "Choose LOI..." : "Select Vendor First"}
                  isDisabled={!selectedVendor}
                />
              </div>
            </div>
          </div>

          {loiDetails && (
            <div className={[styles['po-summary-card'], styles['card']].join(' ')}>
              <div className={styles['summary-info-grid']}>
                <div className={styles['summary-item']}>
                  <label>LOI NUMBER</label>
                  <div className={[styles['value'], styles['po-highlight']].join(' ')}>{loiDetails.loi_number || '---'}</div>
                </div>
                <div className={styles['summary-item']}>
                  <label>VENDOR</label>
                  <div className={styles['value']}>{loiDetails.vendor?.vendor_name || '---'}</div>
                </div>
                <div className={styles['summary-item']}>
                  <label>PROJECT</label>
                  <div className={styles['value']}>{loiDetails.project?.project_name || '---'}</div>
                </div>
                <div className={styles['summary-item']}>
                  <label>STATUS</label>
                  <div className={styles['value']}>{loiDetails.status?.toUpperCase() || '---'}</div>
                </div>
                <div className={styles['summary-item']}>
                  <label>AMOUNT</label>
                  <div className={styles['value']}>₹{parseFloat(loiDetails.total_amount || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className={styles['summary-item']}>
                  <label>ISSUE DATE</label>
                  <div className={styles['value']}>{loiDetails.loi_date ? new Date(loiDetails.loi_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}</div>
                </div>
              </div>
              <div className={styles['summary-visual']}>
                <div className={styles['drawing-frame']}>
                  {loiDetails?.po_document_filepath ? (
                    // Show preview of the stored LOI document - persistent even when new design is uploaded
                    loiDetails.po_document_filepath.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <div className={styles['existing-preview-wrapper']}>
                        <img
                          src={getStorageUrl(loiDetails.po_document_filepath)}
                          alt="Stored LOI Document"
                        />
                        <span className={styles['existing-badge']}>STORED IMAGE</span>
                      </div>
                    ) : (
                      <div className={styles['pdf-preview-small']}>
                        <FileText size={40} className={styles['pdf-icon-preview']} />
                        <span className={styles['pdf-label']}>STORED PDF</span>
                      </div>
                    )
                  ) : mainFile && previewUrl ? (
                    // Show new design preview only if no LOI document exists
                    mainFile.type.startsWith('image/') ? (
                      <img src={previewUrl} alt="Design Preview" />
                    ) : (
                      <div className={styles['pdf-preview-small']}>
                        <FileText size={40} className={styles['pdf-icon-preview']} />
                        <span className={styles['pdf-label']}>NEW PDF PREVIEW</span>
                      </div>
                    )
                  ) : (
                    <ImageIcon size={48} className={styles['placeholder-icon']} />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={styles['upload-form-grid']}>
            <div className={[styles['form-section'], styles['card']].join(' ')}>
              <h3 className={styles['section-title']}>Design Details</h3>
              {/* <div className={styles['form-group']}>
                <label><Briefcase size={16} /> Drawing Type <span className={styles['required-star']}>*</span></label>
                <select
                  className={styles['design-input']}
                  name="drawingType"
                  value={formData.drawingType}
                  onChange={handleInputChange}
                >
                  <option>Architectural Plans</option>
                  <option>Structural Diagrams</option>
                  <option>Electrical Layouts</option>
                  <option>Plumbing Details</option>
                </select>
              </div> */}

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Tag size={16} /> Version/Revision <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    className={styles['design-input']}
                    placeholder="e.g. 1.0"
                    name="version"
                    value={formData.version}
                    readOnly
                    style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                  />
                </div>
                <div className={styles['form-group']}>
                  {/* <label><Info size={16} /> Revision Code <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    className={styles['design-input']}
                    placeholder="e.g. REV-B"
                    name="revisionCode"
                    value={formData.revisionCode}
                    onChange={handleInputChange}
                  /> */}
                </div>
              </div>

              <div className={styles['form-group']}>
                <label><FileText size={16} /> Submission Notes</label>
                <textarea
                  className={styles['design-textarea']}
                  placeholder="Public notes for the reviewer regarding this revision..."
                  name="submissionNotes"
                  value={formData.submissionNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date receiving design from vendor <span className={styles['required-star']}>*</span></label>
                  <CustomDatePicker
                    selected={formData.vendorReceivedDate}
                    onChange={(date) => handleDateChange('vendorReceivedDate', date)}
                    placeholderText="Select date"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date of Submission to Client <span className={styles['required-star']}>*</span></label>
                  <CustomDatePicker
                    selected={formData.clientSubmissionDate}
                    onChange={(date) => handleDateChange('clientSubmissionDate', date)}
                    placeholderText="Select date"
                  />
                </div>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date of Submission to Consultant</label>
                  <CustomDatePicker
                    selected={formData.consultantSubmissionDate}
                    onChange={(date) => handleDateChange('consultantSubmissionDate', date)}
                    placeholderText="Select date"
                  />
                </div>
                <div className={styles['form-group']}></div>
              </div>
              <div className={styles['form-group']}>
                <label><AlertCircle size={16} /> Internal Office Notes (Optional)</label>
                <textarea
                  className={styles['design-textarea']}
                  placeholder="Private internal notes for record keeping..."
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>


            </div>

            <div className={[styles['form-section'], styles['card']].join(' ')}>
              <h3 className={styles['section-title']}>Files</h3>
              <div className={styles['form-group']}>
                <label><FileUp size={16} /> Main Design Document (PDF)</label>
                <div
                  className={`${styles['upload-dropzone-large']} ${mainFile ? styles['has-file'] : ''}`}
                  onClick={() => !mainFile && mainFileInputRef.current.click()}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                >
                  <input
                    type="file"
                    ref={mainFileInputRef}
                    style={{ display: 'none' }}
                    accept=".pdf,image/*"
                    onChange={handleMainFileChange}
                  />

                  {mainFile ? (
                    <div className={styles['preview-container-main']}>
                      {mainFile.type === 'application/pdf' ? (
                        <div className={styles['pdf-preview-wrapper']}>
                          <iframe
                            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                            className={styles['pdf-iframe']}
                            title="PDF Preview"
                          />
                          <div className={styles['file-overlay-info']}>
                            <FileText size={20} color="#ef4444" />
                            <span>{mainFile.name} ({(mainFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                        </div>
                      ) : mainFile.type.startsWith('image/') ? (
                        <div className={styles['image-preview-wrapper-main']}>
                          <img src={previewUrl} alt="Design Preview" className={styles['image-preview-main']} />
                        </div>
                      ) : (
                        <div className={styles['file-generic-preview']}>
                          <FileUp size={48} />
                          <p>{mainFile.name}</p>
                        </div>
                      )}

                      <div className={styles['preview-overlay-actions']}>
                        <button
                          type="button"
                          className={styles['remove-btn-overlay']}
                          onClick={removeMainFile}
                        >
                          <X size={18} />
                          <span>Remove Document</span>
                        </button>
                        <button
                          type="button"
                          className={styles['change-btn-overlay']}
                          onClick={() => mainFileInputRef.current.click()}
                        >
                          <FileUp size={18} />
                          <span>Replace Document</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles['upload-icon-box']}>
                        <FileUp size={28} />
                      </div>
                      <div className={styles['upload-text']}>
                        <h4>Drag & drop your file here</h4>
                        <p>PDF or Images (JPG, PNG) supported</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className={styles['form-group']}>
                <label><Paperclip size={16} /> Optional: Marked Drawings / Supporting Docs</label>
                <div className={styles['file-selector-input']}>
                  <input
                    type="file"
                    ref={supportingFilesInputRef}
                    style={{ display: 'none' }}
                    multiple
                    onChange={handleSupportingFilesChange}
                  />
                  <div className={styles['file-info-placeholder']}>
                    <Paperclip size={18} />
                    <span>
                      {supportingFiles.length > 0
                        ? `${supportingFiles.length} file(s) selected`
                        : 'No files selected'}
                    </span>
                  </div>
                  <button
                    className={styles['btn-select-files']}
                    onClick={(e) => {
                      e.preventDefault();
                      supportingFilesInputRef.current.click();
                    }}
                  >
                    SELECT FILES
                  </button>
                </div>
                {supportingFiles.length > 0 && (
                  <div className={styles['selected-files-list']}>
                    {supportingFiles.map((file, idx) => (
                      <div key={idx} className={styles['file-tag']}>
                        <span>{file.name}</span>
                        <X size={14} onClick={(e) => {
                          e.stopPropagation();
                          setSupportingFiles(supportingFiles.filter((_, i) => i !== idx));
                        }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles['header-actions-btns']}>
            <button type="button" className={styles['btn-cancel']} onClick={() => navigate(-1)} disabled={loading}>
              CANCEL
            </button>
            <button
              type="button"
              className={styles['btn-draft']}
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
            >
              {loading ? <Loader2 className={styles['spinner']} size={18} /> : <SaveIcon size={18} />}
              <span>SAVE & ADD ANOTHER</span>
            </button>
            <button
              type="button"
              className={styles['btn-primary']}
              onClick={(e) => handleSubmit(e, false)}
              disabled={loading || !selectedPO}
            >
              {loading ? <Loader2 className={styles['spinner']} size={18} /> : <CheckCircle2 size={18} />}
              <span>SUBMIT FOR REVIEW</span>
            </button>
          </div>

        </main>
      </div>
    </div>
  );
};

export default UploadDesign;
